import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Alert,
  Snackbar,
  Chip,
  Grid,
  CircularProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  CloudUpload as CloudUploadIcon
} from '@mui/icons-material';

const AdminAdvertisements = () => {
  const [advertisements, setAdvertisements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState('add'); // 'add' 或 'edit'
  const [currentAdvertisement, setCurrentAdvertisement] = useState(null);
  const [formData, setFormData] = useState({
    id: '',
    name: '',
    description: '',
    imageUrl: '',
    clickUrl: '',
    isActive: true,
    duration: 3000,
    allowClose: true,
    position: 'fullscreen',
    priority: 1,
    startDate: new Date().toISOString().split('T')[0],
    endDate: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    views: 0,
    clicks: 0
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // 获取广告列表
  useEffect(() => {
    fetchAdvertisements();
  }, []);

  const fetchAdvertisements = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/advertisements');
      if (!response.ok) {
        throw new Error('获取广告列表失败');
      }
      const result = await response.json();
      setAdvertisements(result.data || []);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // 打开添加广告对话框
  const handleAddClick = () => {
    setDialogMode('add');
    setCurrentAdvertisement(null);
    setFormData({
      id: `ad_${Date.now()}`,
      name: '',
      description: '',
      imageUrl: '',
      clickUrl: '',
      isActive: true,
      duration: 3000,
      allowClose: true,
      position: 'fullscreen',
      priority: 1,
      startDate: new Date().toISOString().split('T')[0],
      endDate: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      views: 0,
      clicks: 0
    });
    // 重置图片状态
    setImageFile(null);
    setImagePreview(null);
    setOpenDialog(true);
  };

  // 打开编辑广告对话框
  const handleEditClick = (advertisement) => {
    setDialogMode('edit');
    setCurrentAdvertisement(advertisement);
    setFormData({
      id: advertisement.id || `ad_${Date.now()}`,
      name: advertisement.name || '',
      description: advertisement.description || '',
      imageUrl: advertisement.imageUrl || '',
      clickUrl: advertisement.clickUrl || '',
      isActive: advertisement.isActive !== false,
      duration: advertisement.duration || 3000,
      allowClose: advertisement.allowClose !== false,
      position: advertisement.position || 'fullscreen',
      priority: advertisement.priority || 1,
      startDate: advertisement.startDate || new Date().toISOString().split('T')[0],
      endDate: advertisement.endDate || new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      views: advertisement.views || 0,
      clicks: advertisement.clicks || 0
    });
    // 重置图片状态
    setImageFile(null);
    setImagePreview(null);
    setOpenDialog(true);
  };

  // 删除广告
  const handleDeleteClick = async (advertisementId) => {
    if (window.confirm('确定要删除这个广告吗？')) {
      try {
        const response = await fetch(`/api/advertisements/${advertisementId}`, {
          method: 'DELETE',
        });
        if (!response.ok) {
          throw new Error('删除广告失败');
        }
        const result = await response.json();
        setSnackbar({
          open: true,
          message: result.message || '广告删除成功',
          severity: 'success'
        });
        fetchAdvertisements();
      } catch (error) {
        setSnackbar({
          open: true,
          message: error.message,
          severity: 'error'
        });
      }
    }
  };

  // 处理图片选择
  const handleImageChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setImageFile(file);
      // 创建图片预览
      const reader = new FileReader();
      reader.onload = (e) => {
        const imageDataUrl = e.target.result;
        setImagePreview(imageDataUrl);
        // 更新 formData 中的 imageUrl 字段
        setFormData(prev => ({
          ...prev,
          imageUrl: imageDataUrl
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  // 处理图片上传
  const handleImageUpload = async () => {
    if (!imageFile) {
      return formData.imageUrl; // 如果没有选择新图片，返回现有图片地址
    }

    const formDataForUpload = new FormData();
    formDataForUpload.append('file', imageFile);

    try {
      const response = await fetch('/api/advertisements/upload', {
        method: 'POST',
        body: formDataForUpload
      });

      if (!response.ok) {
        throw new Error('图片上传失败');
      }

      const result = await response.json();
      return result.data.file_url; // 修改：使用正确的字段名
    } catch (error) {
      setSnackbar({
        open: true,
        message: '图片上传失败: ' + error.message,
        severity: 'error'
      });
      return null;
    }
  };

  // 保存广告
  const handleSave = async () => {
    try {
      // 先上传图片（如果有选择新图片）
      const imageUrl = await handleImageUpload();
      if (imageUrl === null) {
        return; // 如果图片上传失败，停止保存
      }

      const method = dialogMode === 'add' ? 'POST' : 'PUT';
      const url = dialogMode === 'add' ? '/api/advertisements' : `/api/advertisements/${currentAdvertisement.id}`;

      const response = await fetch(url, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          imageUrl: imageUrl
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || (dialogMode === 'add' ? '添加广告失败' : '更新广告失败'));
      }

      const result = await response.json();
      setSnackbar({
        open: true,
        message: result.message || (dialogMode === 'add' ? '广告添加成功' : '广告更新成功'),
        severity: 'success'
      });
      setOpenDialog(false);
      fetchAdvertisements();
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message,
        severity: 'error'
      });
    }
  };

  // 表单输入变化
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // 关闭对话框
  const handleCloseDialog = () => {
    setOpenDialog(false);
    // 重置图片状态
    setImageFile(null);
    setImagePreview(null);
  };

  // 关闭 snackbar
  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // 渲染加载状态
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  // 渲染错误状态
  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 3 }}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleAddClick}
          sx={{
            background: 'linear-gradient(45deg, #0f4c81, #4a90a4)',
            boxShadow: '0 3px 10px rgba(15, 76, 129, 0.4)',
            '&:hover': {
              background: 'linear-gradient(45deg, #0a3a64, #0f4c81)',
              boxShadow: '0 4px 15px rgba(15, 76, 129, 0.5)',
            },
          }}
        >
          新增广告
        </Button>
      </Box>

      {/* 广告统计卡片 */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2, display: 'flex', alignItems: 'center' }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" color="text.secondary">总广告数</Typography>
              <Typography variant="h4">{advertisements.length}</Typography>
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2, display: 'flex', alignItems: 'center' }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" color="text.secondary">激活广告</Typography>
              <Typography variant="h4">{advertisements.filter(ad => ad.isActive).length}</Typography>
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2, display: 'flex', alignItems: 'center' }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" color="text.secondary">总点击次数</Typography>
              <Typography variant="h4">{advertisements.reduce((sum, ad) => sum + (ad.clicks || 0), 0)}</Typography>
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2, display: 'flex', alignItems: 'center' }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" color="text.secondary">总浏览次数</Typography>
              <Typography variant="h4">{advertisements.reduce((sum, ad) => sum + (ad.views || 0), 0)}</Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* 广告列表表格 */}
      <TableContainer component={Paper} sx={{ mt: 3 }}>
        <Table>
          <TableHead sx={{ background: '#f5f5f5' }}>
            <TableRow>
              <TableCell>广告名称</TableCell>
              <TableCell>描述</TableCell>
              <TableCell>位置</TableCell>
              <TableCell>优先级</TableCell>
              <TableCell>状态</TableCell>
              <TableCell>点击数</TableCell>
              <TableCell>浏览数</TableCell>
              <TableCell align="center">操作</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {advertisements.map((advertisement) => (
              <TableRow key={advertisement.id} hover>
                <TableCell>
                  <Box display="flex" alignItems="center">
                    <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                      {advertisement.name}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {advertisement.description || '无描述'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip 
                    label={advertisement.position === 'fullscreen' ? '全屏' : advertisement.position}
                    size="small"
                    color={advertisement.position === 'fullscreen' ? 'primary' : 'default'}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {advertisement.priority}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip 
                    icon={advertisement.isActive ? <VisibilityIcon /> : <VisibilityOffIcon />}
                    label={advertisement.isActive ? '激活' : '未激活'}
                    size="small"
                    color={advertisement.isActive ? 'success' : 'default'}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{advertisement.clicks || 0}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{advertisement.views || 0}</Typography>
                </TableCell>
                <TableCell align="center">
                  <IconButton 
                    size="small" 
                    color="primary" 
                    onClick={() => handleEditClick(advertisement)}
                    sx={{ mr: 1 }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton 
                    size="small" 
                    color="error" 
                    onClick={() => handleDeleteClick(advertisement.id)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* 广告详情对话框 */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: '12px' } }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(45deg, #0f4c81, #4a90a4)', 
          color: 'white',
          borderRadius: '12px 12px 0 0'
        }}>
          {dialogMode === 'add' ? '新增广告' : '编辑广告'}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="广告名称"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="广告位置"
                name="position"
                value={formData.position}
                onChange={handleInputChange}
                required
                variant="outlined"
                select
                SelectProps={{
                  native: true
                }}
              >
                <option value="fullscreen">全屏</option>
                <option value="banner">横幅</option>
                <option value="popup">弹窗</option>
                <option value="interstitial">插屏</option>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="优先级"
                name="priority"
                type="number"
                value={formData.priority}
                onChange={handleInputChange}
                required
                variant="outlined"
                inputProps={{ min: 1, max: 10 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="持续时间(ms)"
                name="duration"
                type="number"
                value={formData.duration}
                onChange={handleInputChange}
                required
                variant="outlined"
                inputProps={{ min: 1000, step: 1000 }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="广告描述"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                multiline
                rows={2}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  广告图片
                </Typography>
                <input
                  accept="image/*"
                  style={{ display: 'none' }}
                  id="image-upload"
                  type="file"
                  onChange={handleImageChange}
                />
                <label htmlFor="image-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    fullWidth
                    startIcon={<CloudUploadIcon />}
                    sx={{
                      borderRadius: '8px',
                      py: 1.5,
                      borderStyle: imageFile ? 'dashed' : 'solid',
                      borderColor: imageFile ? 'primary.main' : 'grey.300'
                    }}
                  >
                    {imageFile ? '已选择文件: ' + imageFile.name : '选择图片文件'}
                  </Button>
                </label>
                {imagePreview && (
                  <Box sx={{ mt: 2, textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                      图片预览
                    </Typography>
                    <img
                      src={imagePreview}
                      alt="预览"
                      style={{
                        maxWidth: '100%',
                        maxHeight: '200px',
                        borderRadius: '8px',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                      }}
                    />
                  </Box>
                )}
                {formData.imageUrl && !imageFile && (
                  <Box sx={{ mt: 2, textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                      当前图片
                    </Typography>
                    <img
                      src={formData.imageUrl}
                      alt="当前"
                      style={{
                        maxWidth: '100%',
                        maxHeight: '200px',
                        borderRadius: '8px',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                      }}
                    />
                  </Box>
                )}
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="点击跳转地址"
                name="clickUrl"
                value={formData.clickUrl}
                onChange={handleInputChange}
                required
                variant="outlined"
                placeholder="https://example.com"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="开始日期"
                name="startDate"
                type="date"
                value={formData.startDate}
                onChange={handleInputChange}
                required
                variant="outlined"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="结束日期"
                name="endDate"
                type="date"
                value={formData.endDate}
                onChange={handleInputChange}
                required
                variant="outlined"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive}
                    onChange={handleInputChange}
                    name="isActive"
                    color="primary"
                  />
                }
                label="激活"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.allowClose}
                    onChange={handleInputChange}
                    name="allowClose"
                    color="primary"
                  />
                }
                label="允许关闭"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ 
          background: '#f5f5f5', 
          borderRadius: '0 0 12px 12px',
          p: 2
        }}>
          <Button onClick={handleCloseDialog} color="inherit">
            取消
          </Button>
          <Button 
            onClick={handleSave} 
            variant="contained" 
            color="primary"
            sx={{
              background: 'linear-gradient(45deg, #1976d2, #42a5f5)',
              boxShadow: '0 3px 10px rgba(25, 118, 210, 0.4)',
              '&:hover': {
                background: 'linear-gradient(45deg, #1565c0, #1976d2)',
                boxShadow: '0 4px 15px rgba(25, 118, 210, 0.5)',
              },
            }}
          >
            保存
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{
            width: '100%',
            borderRadius: '8px'
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminAdvertisements;